import { useEffect, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import CategoryMultiSelectWrapper from 'Deer/components/CategoryMultiSelect/CategoryMultiSelectWrapper';
import { useQuery } from 'components/hooks/useQuery';
import { useDispatch } from 'react-redux';
import { REVIEW_FORM_STAGE, setReviewCreationKey, useReviewCreationV1Data } from 'reducers/reviewCreationV1Slice';
import { checkEnabledSimplifiedReviewDemoPresets } from 'utils/helpers/featureFlags';

import { PARTICIPANTS_SELECTION_MODULES } from 'constants/ProjectConstants';

import { InputV2 } from 'uiToolkitV1/InputV2';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { LabelFieldWrapper } from '../components/LabelFieldWrapper';
import { SectionWrapper } from '../components/SectionWrapper';
import {
  CONTINUOUS_REVIEW_TYPES,
  REVIEW_CREATION_TYPES,
  REVIEW_PRESET_TYPES,
  REVIEW_TYPE_OPTIONS,
} from '../constants/reviewCreationConstants';
import { useUpdateReviewName, useUpdateReviewType } from '../dataHooks/nameAndRevieweesSectionHooks';
import { ContinuousReviewSettings } from './nameAndRevieweesComps/ContinuousReviewSettings';

// import { LaunchDaysSelect } from './nameAndRevieweesComps/LaunchDaysSelect';

const CONTINUOUS_REVIEW_TYPE_OPTIONS = [
  {
    label: 'Work anniversary review',
    value: CONTINUOUS_REVIEW_TYPES.WORK_ANNIVERSARY,
    subLabel: "Launched around the employee's work-anniversary",
  },
  {
    label: 'Probation review (single instance)',
    value: CONTINUOUS_REVIEW_TYPES.DATE_OF_JOINING,
    subLabel: "Launched at the end of the employee's probation period",
  },
  {
    label: 'Probation check-in (30-60-90)',
    value: CONTINUOUS_REVIEW_TYPES.PROBATION_30_60_90,
    subLabel: 'Launched on the 30th, 60th, and 90th day post joining',
  },
  {
    label: 'Based on Review Outcome',
    value: CONTINUOUS_REVIEW_TYPES.PIP,
    subLabel: 'Employee qualifies upon receiving a specific outcome in a review cycle',
  },
];

const PRESET_TO_CONTINUOUS_REVIEW_TYPE_MAP = {
  [REVIEW_PRESET_TYPES.WORK_ANNIVERSARY_REVIEW]: CONTINUOUS_REVIEW_TYPES.WORK_ANNIVERSARY,
  [REVIEW_PRESET_TYPES.PROBATION_REVIEW]: CONTINUOUS_REVIEW_TYPES.DATE_OF_JOINING,
  [REVIEW_PRESET_TYPES.PROBATION_30_60_90]: CONTINUOUS_REVIEW_TYPES.PROBATION_30_60_90,
  [REVIEW_PRESET_TYPES.PIP_REVIEW]: CONTINUOUS_REVIEW_TYPES.PIP,
};

const PIPEventSelection = () => {
  const dispatch = useDispatch();
  const { pipEventReviewCycle, pipEventType } = useReviewCreationV1Data();

  const PIP_REVIEW_CYCLES = [
    {
      label: 'Employee Probation Review 2025',
      value: 'Employee Probation Review 2025',
    },
    {
      label: 'Annual Appraisal 2025',
      value: 'Annual Appraisal 2025',
    },
  ];

  const PIP_EVENT_TYPES = [
    {
      label: 'Confirm',
      value: 'confirm',
    },
    {
      label: 'PIP',
      value: 'PIP',
    },
    {
      label: 'Extension of Probation',
      value: 'extension_of_probation',
    },
    {
      label: 'Terminate',
      value: 'terminate',
    },
    {
      label: 'Promote',
      value: 'promote',
    },
    {
      label: 'No Change',
      value: 'no_change',
    },
    {
      label: 'Future Consideration',
      value: 'future_consideration',
    },
  ];

  const onChangePipReviewCycle = ({ value }) => {
    dispatch(setReviewCreationKey({ pipEventReviewCycle: value }));
  };

  const onChangePipEventType = ({ value }) => {
    dispatch(setReviewCreationKey({ pipEventType: value }));
  };

  return (
    <Flex alignItems="baseline" mt={2} gap={2}>
      <Box flexShrink={0}>
        <TextBodyMedium>Add if final outcome in </TextBodyMedium>
      </Box>
      <SelectDashed
        value={PIP_REVIEW_CYCLES.find(({ value }) => pipEventReviewCycle === value)}
        options={PIP_REVIEW_CYCLES}
        handleChange={onChangePipReviewCycle}
      />
      is
      <SelectDashed
        value={PIP_EVENT_TYPES.find(({ value }) => pipEventType === value)}
        options={PIP_EVENT_TYPES}
        handleChange={onChangePipEventType}
      />
    </Flex>
  );
};

export const NameAndReviewees = () => {
  const dispatch = useDispatch();
  const {
    reviewCycleName,
    isDuplicateName,
    isDuplicateNameError,
    reviewType,
    reviewStage,
    totalEmployeeCount,
    revieweeSelectionType,
    selectedFilters,
    reviewCycleId,
    continuousReviewType,
  } = useReviewCreationV1Data();
  const { mutate: updateReviewName } = useUpdateReviewName();
  const { mutate: updateReviewType } = useUpdateReviewType();

  const { preset } = useQuery();

  // demo data label override
  if (checkEnabledSimplifiedReviewDemoPresets()) {
    const continuousReviewTypeOption = REVIEW_TYPE_OPTIONS.find(
      ({ value }) => value === REVIEW_CREATION_TYPES.TRIGGERED
    );
    continuousReviewTypeOption.label = 'Continuous (based on triggers)';
  }

  const [enableValidation, setEnableValidation] = useState(false);

  const isDraft = reviewStage === REVIEW_FORM_STAGE.DRAFT;

  const onChangeName = (e) => {
    dispatch(setReviewCreationKey({ reviewCycleName: e.target.value }));
  };

  const handleNameBlur = (e) => {
    setEnableValidation(true);
    updateReviewName({ reviewCycleName: e.target.value });
  };

  const onChangeType = ({ value }) => {
    dispatch(setReviewCreationKey({ reviewType: value }));
    updateReviewType({ reviewType: value });
  };

  const onChangeContinuousReviewType = ({ value }) => {
    dispatch(setReviewCreationKey({ continuousReviewType: value }));
  };

  const handleCategoryMetaDataChange = (updatedMetaData) => {
    dispatch(setReviewCreationKey(updatedMetaData));
  };

  useEffect(() => {
    if (!checkEnabledSimplifiedReviewDemoPresets() || !preset) return;

    dispatch(setReviewCreationKey({ continuousReviewType: PRESET_TO_CONTINUOUS_REVIEW_TYPE_MAP[preset] }));
  }, []);

  return (
    <SectionWrapper
      title="Review name, type and participants"
      subTitle="Review Name, Review Type, Participants, Reviewers"
      restrictAccess={!isDraft}
    >
      <LabelFieldWrapper label="Review cycle name">
        <InputV2
          placeholder="Enter a cycle name"
          isDashed
          onChange={onChangeName}
          onBlur={handleNameBlur}
          value={reviewCycleName}
          width="100%"
          errorText={enableValidation && isDuplicateName ? isDuplicateNameError : ''}
          isError={enableValidation && isDuplicateName}
        />
      </LabelFieldWrapper>

      <LabelFieldWrapper label="Type of review">
        <Box>
          <SelectDashed
            value={REVIEW_TYPE_OPTIONS.find(({ value }) => reviewType === value)}
            options={REVIEW_TYPE_OPTIONS}
            handleChange={onChangeType}
            width="100%"
          />
          {checkEnabledSimplifiedReviewDemoPresets() && reviewType === REVIEW_CREATION_TYPES.TRIGGERED && (
            <Flex alignItems="baseline" mt={2} gap={2}>
              <Box flexShrink={0}>
                <TextBodyMedium color="text.g2">Type</TextBodyMedium>
              </Box>
              <SelectDashed
                value={CONTINUOUS_REVIEW_TYPE_OPTIONS.find(({ value }) => continuousReviewType === value)}
                options={CONTINUOUS_REVIEW_TYPE_OPTIONS}
                handleChange={onChangeContinuousReviewType}
                fontStyle="italic"
              />
            </Flex>
          )}
        </Box>
        {checkEnabledSimplifiedReviewDemoPresets() &&
          reviewType === REVIEW_CREATION_TYPES.TRIGGERED &&
          continuousReviewType === CONTINUOUS_REVIEW_TYPES.PIP && (
            <Box gridColumn="2 / span 2" maxW="70%">
              <PIPEventSelection />
            </Box>
          )}
      </LabelFieldWrapper>

      {/* {checkEnabledSimplifiedReviewDemoPresets() && reviewType === REVIEW_CREATION_TYPES.TRIGGERED && (
        <LabelFieldWrapper label="Launch">
          <LaunchDaysSelect />
        </LabelFieldWrapper>
      )} */}

      <LabelFieldWrapper label="Who is part of the review">
        {reviewType === REVIEW_CREATION_TYPES.TRIGGERED ? (
          <ContinuousReviewSettings />
        ) : (
          <CategoryMultiSelectWrapper
            metaData={{ totalEmployeeCount, revieweeSelectionType, selectedFilters, reviewCycleId }}
            setMetaData={handleCategoryMetaDataChange}
            module={PARTICIPANTS_SELECTION_MODULES.REVIEW}
          />
        )}
      </LabelFieldWrapper>
    </SectionWrapper>
  );
};
